import { auth } from '../firebase';
import type { CompleteStepResult, ContentBlock, Step, StepType, Track } from '../types';

// Authed fetch helper for the Functions API. Never throws on HTTP errors —
// always resolves to { ok, status, data }. Identity travels as a Bearer ID
// token; the server derives the user from the verified token, never the body.

export interface ApiResult<T = unknown> {
  ok: boolean;
  status: number;
  data: T;
}

async function token(forceRefresh = false): Promise<string | null> {
  const u = auth.currentUser;
  if (!u) return null;
  try {
    return await u.getIdToken(forceRefresh);
  } catch {
    return null;
  }
}

/**
 * Authed binary GET — fetches a same-origin API path with the Bearer ID token and
 * returns the response Blob. Used by the PDF deck to load an uploaded slide PDF
 * through the access-checked same-origin proxy (`/api/track/:t/step/:s/pdf`) so
 * pdf.js never makes a cross-origin (CORS-prone) request. Retries ONCE on 401 with
 * a forced token refresh (covers long-lived tabs / recently-changed claims).
 * Never throws — resolves to { ok, status, blob? }.
 */
export async function fetchAuthedBlob(
  path: string,
  signal?: AbortSignal,
): Promise<{ ok: boolean; status: number; blob?: Blob }> {
  const t = await token();
  if (!t) return { ok: false, status: 0 };
  const doFetch = (tok: string) =>
    fetch(path, { method: 'GET', credentials: 'omit', signal, headers: { Authorization: `Bearer ${tok}` } });
  try {
    let res = await doFetch(t);
    if (res.status === 401) {
      const t2 = await token(true); // forced refresh, then one retry
      if (t2) res = await doFetch(t2);
    }
    if (!res.ok) return { ok: false, status: res.status };
    return { ok: true, status: res.status, blob: await res.blob() };
  } catch {
    return { ok: false, status: 0 };
  }
}

export async function api<T = unknown>(
  method: string,
  path: string,
  body?: Record<string, unknown>,
): Promise<ApiResult<T>> {
  const t = await token();
  if (!t) return { ok: false, status: 0, data: { error: 'no_token' } as T };

  const init: RequestInit = {
    method,
    credentials: 'omit',
    headers: {
      Authorization: `Bearer ${t}`,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };

  try {
    const res = await fetch(path, init);
    const text = await res.text();
    let data: T;
    try {
      data = (text.length > 0 ? JSON.parse(text) : {}) as T;
    } catch {
      data = { _raw: text } as T;
    }
    return { ok: res.ok, status: res.status, data };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error';
    return { ok: false, status: 0, data: { error: 'network', message } as T };
  }
}

// ── Typed endpoints ──────────────────────────────────────────────────────────

export interface MeResponse {
  uid: string;
  email: string;
  displayName: string;
  initials: string;
  role: 'member' | 'admin';
  audience: 'staff' | 'candidate';
  isStaff: boolean;
  isMentor: boolean;
  showOnBoard: boolean;
  /** Candidate's granted trails (∪ Welcome). Empty for staff. */
  accessTrackIds: string[];
  claimsUpdated: boolean;
  /** The caller's assigned Ustad (mentor); null if none. */
  mentor?: { displayName: string; initials: string } | null;
}

export const getMe = () => api<MeResponse>('GET', '/api/me');

// ── Catalog (API-served; candidates can't read tracks directly) ──────────────

export interface CatalogTrack extends Track {
  steps: Step[];
}

export const getCatalog = () => api<{ tracks: CatalogTrack[] }>('GET', '/api/catalog');

export const getCatalogTrack = (trackId: string) =>
  api<{ track: CatalogTrack }>('GET', `/api/track/${encodeURIComponent(trackId)}`);

export const completeStep = (trackId: string, stepId: string, reflection: string, evidenceUrl?: string) =>
  api<CompleteStepResult>('POST', '/api/steps/complete', {
    trackId,
    stepId,
    reflection,
    ...(evidenceUrl ? { evidenceUrl } : {}),
  });

export const reactToActivity = (activityId: string) =>
  api<{ ok: boolean; hearts: number; reacted: boolean; changed: boolean }>(
    'POST',
    '/api/activity/react',
    { activityId },
  );

// ── Mentor endpoints ─────────────────────────────────────────────────────────

export interface MenteeTrail {
  trackId: string;
  title: string;
  status: string;
  completedSteps: number;
  totalSteps: number;
}

export interface Mentee {
  uid: string;
  displayName: string;
  initials: string;
  assignedCount: number;
  trails: MenteeTrail[];
}

export const getMentees = () => api<{ mentees: Mentee[] }>('GET', '/api/mentor/mentees');

// ── Admin endpoints ──────────────────────────────────────────────────────────

export interface AdminUser {
  uid: string;
  email: string;
  displayName: string;
  initials: string;
  role: 'member' | 'admin';
  isMentor: boolean;
  showOnBoard: boolean;
  mentorId: string | null;
  points: { allTime: number; month: number; monthKey: string };
  streak: { count: number; lastActiveDate: string | null };
  /** Trail ids this person is enrolled in (their progress docs). */
  assignedTrackIds: string[];
}

export const getAdminUsers = () =>
  api<{ users: AdminUser[] }>('GET', '/api/admin/users');

export const setUserRole = (uid: string, role: 'member' | 'admin', force?: boolean) =>
  api<{ ok: boolean; changed: boolean; role: string; claimsSynced: boolean }>(
    'POST',
    '/api/admin/users/role',
    { uid, role, ...(force ? { force: true } : {}) },
  );

export const setUserMentor = (uid: string, isMentor: boolean) =>
  api<{ ok: boolean }>('POST', '/api/admin/users/mentor', { uid, isMentor });

export const setUserBoard = (uid: string, showOnBoard: boolean) =>
  api<{ ok: boolean }>('POST', '/api/admin/users/board', { uid, showOnBoard });

export const setUserMentorId = (uid: string, mentorId: string | null) =>
  api<{ ok: boolean }>('POST', '/api/admin/users/mentorId', { uid, mentorId });

export const assignTrack = (uid: string, trackId: string) =>
  api<{ ok: boolean; created: boolean }>('POST', '/api/admin/users/assign', { uid, trackId });

// ── Candidate invites (Aspirant lane) ────────────────────────────────────────

export interface AdminInvite {
  id: string;
  kind: 'email' | 'domain';
  value: string;
  accessTrackIds: string[];
  label: string | null;
  status: 'active' | 'revoked';
  expiresAt: string | null;
  createdAt: string | null;
  audience: 'candidate' | 'staff';
}

export const getInvites = () => api<{ invites: AdminInvite[] }>('GET', '/api/admin/invites');

export const createInvite = (body: {
  kind: 'email' | 'domain';
  value: string;
  accessTrackIds: string[];
  label?: string;
  expiresAt?: string;
  sendEmail?: boolean;
}) =>
  api<{
    ok: boolean;
    id: string;
    emailSent?: 'sent' | 'no_smtp' | 'blocked' | 'error' | 'skipped';
    audience?: 'candidate' | 'staff';
    assignedNow?: number;
    error?: string;
    detail?: string;
  }>('POST', '/api/admin/invites', body);

export const resendInvite = (id: string) =>
  api<{ ok: boolean; sent?: boolean; error?: string }>(
    'POST',
    `/api/admin/invites/${encodeURIComponent(id)}/send`,
  );

export const updateInvite = (
  id: string,
  body: { status?: 'active' | 'revoked'; accessTrackIds?: string[]; expiresAt?: string | null; label?: string | null },
) => api<{ ok: boolean; error?: string }>('PATCH', `/api/admin/invites/${encodeURIComponent(id)}`, body);

export const deleteInvite = (id: string) =>
  api<{ ok: boolean; error?: string }>('DELETE', `/api/admin/invites/${encodeURIComponent(id)}`);

// Staff (teammate) course grants: edited via /admin/staff-grants, not /admin/invites.
export const updateStaffGrant = (
  id: string,
  body: { status?: 'active' | 'revoked'; accessTrackIds?: string[]; label?: string | null },
) =>
  api<{ ok: boolean; assignedNow?: number; error?: string }>(
    'PATCH',
    `/api/admin/staff-grants/${encodeURIComponent(id)}`,
    body,
  );

export const deleteStaffGrant = (id: string) =>
  api<{ ok: boolean; error?: string }>('DELETE', `/api/admin/staff-grants/${encodeURIComponent(id)}`);

import type { PersonDetail, PersonRow } from './people-types';
export type { DayRow, PersonDetail, PersonMitti, PersonRow, PersonTrack } from './people-types';

export const getPeople = () =>
  api<{ people: PersonRow[]; weekStart: string; today: string; truncated?: boolean }>('GET', '/api/admin/people');

export const getPerson = (uid: string) =>
  api<PersonDetail>('GET', `/api/admin/people/${encodeURIComponent(uid)}`);

export const setPersonExempt = (uid: string, exempt: boolean) =>
  api<{ ok: boolean; exempt?: boolean; error?: string }>('POST', `/api/admin/people/${encodeURIComponent(uid)}/exempt`, { exempt });

export interface AdminCandidate {
  uid: string;
  email: string;
  displayName: string;
  initials: string;
  accessTrackIds: string[];
  points: { allTime: number; month: number; monthKey: string };
  streak: { count: number; lastActiveDate: string | null };
  createdAt: string | null;
  progress: Record<string, { status: string; completedSteps: number; totalSteps: number }>;
}

export const getCandidates = () => api<{ candidates: AdminCandidate[] }>('GET', '/api/admin/candidates');

// ── Quiz endpoints ───────────────────────────────────────────────────────────

export interface QuizQuestion {
  id: string;
  prompt: string;
  options: string[];
}

export const getQuiz = (trackId: string, stepId: string) =>
  api<{ questions: QuizQuestion[]; masteryMinCorrect?: number }>(
    'GET',
    `/api/quiz?trackId=${encodeURIComponent(trackId)}&stepId=${encodeURIComponent(stepId)}`,
  );

export const scoreQuiz = (
  trackId: string,
  stepId: string,
  answers: Record<string, number>,
  reflection?: string,
) =>
  api<CompleteStepResult>('POST', '/api/quiz/score', {
    trackId,
    stepId,
    answers,
    ...(reflection !== undefined ? { reflection } : {}),
  });

// ── Trail authoring endpoints (admin-only) ───────────────────────────────────

export interface StepInputQuestion {
  id?: string;
  prompt: string;
  options: string[];
  answer: number;
}

export interface StepInput {
  id?: string;
  type: StepType;
  title: string;
  points?: number;
  references?: { label: string; url: string }[];
  // video
  vimeoId?: string;
  vimeoHash?: string;
  youtubeId?: string;
  mediaSrc?: string;
  bunnyVideoId?: string;
  // external
  url?: string;
  preview?: { title?: string; description?: string; image?: string };
  evidence?: 'url' | 'file' | 'none';
  /** 'checker': graded on the learner's Mitti. 'khalifa': signed off by a Khalifa. */
  completion?: 'checker' | 'khalifa';
  assignment?: string;
  // reading
  content?: ContentBlock[];
  // quiz
  questions?: StepInputQuestion[];
  // slides
  slides?: { src: string; alt?: string; caption?: string }[];
  pdfUrl?: string;
}

export interface TrackMetaInput {
  title: string;
  description?: string;
  brand?: string;
  internal?: boolean;
  tags?: string[];
  order?: number;
  published?: boolean;
  badgeId?: string;
}

export const createTrack = (body: TrackMetaInput) =>
  api<{ ok: boolean; id: string }>(
    'POST',
    '/api/admin/tracks',
    body as unknown as Record<string, unknown>,
  );

export const updateTrack = (id: string, body: Partial<TrackMetaInput>) =>
  api<{ ok: boolean; changed: boolean }>(
    'PATCH',
    `/api/admin/tracks/${encodeURIComponent(id)}`,
    body as unknown as Record<string, unknown>,
  );

export const putTrackSteps = (id: string, steps: StepInput[]) =>
  api<{ ok: boolean; stepCount: number }>(
    'PUT',
    `/api/admin/tracks/${encodeURIComponent(id)}/steps`,
    { steps } as unknown as Record<string, unknown>,
  );

// ── Henoyo training environments ─────────────────────────────────────────────

export interface TrainEnv {
  slug: string;
  uid: string;
  email: string;
  displayName: string;
  packs: string[];
  state: string;
  host: string;
  expiresAt: string | null;
  createdAt: string | null;
  createdBy: string;
  lastStartedAt: string | null;
  url: string;
  activeMinutes: number;
  lastActiveAt: string | null;
  packsPending: string[];
  packsInstalling: boolean;
}

export interface TrainEnvSso {
  action?: string;
  ticket?: string;
  expiresAt?: string;
  error?: string;
}

export const getTrainEnvs = () => api<{ envs: TrainEnv[] }>('GET', '/api/admin/train-envs');

export const createTrainEnv = (body: { uid: string; packs: string[]; days: number }) =>
  api<{ ok: boolean; env: TrainEnv; error?: string }>('POST', '/api/admin/train-envs', body);

export const extendTrainEnv = (slug: string) =>
  api<{ ok: boolean; env: TrainEnv; error?: string }>(
    'POST',
    `/api/admin/train-envs/${encodeURIComponent(slug)}/extend`,
  );

export const deleteTrainEnv = (slug: string) =>
  api<{ ok: boolean; error?: string }>('DELETE', `/api/admin/train-envs/${encodeURIComponent(slug)}`);

export const getMyTrainEnv = (opts?: { fresh?: boolean }) =>
  api<{ env: TrainEnv | null }>('GET', opts?.fresh ? '/api/me/train-env?fresh=1' : '/api/me/train-env');

export const startMyTrainEnv = () =>
  api<{
    url: string;
    host: string;
    email: string;
    state: string;
    sso?: { action: string; ticket: string };
    error?: string;
  }>('POST', '/api/me/train-env/start');

export const mintMyTrainEnvSso = () => api<TrainEnvSso>('POST', '/api/me/train-env/sso');

export const addTrainEnvPacks = (slug: string, packs: string[]) =>
  api<{ ok: boolean; env: TrainEnv; error?: string }>(
    'POST',
    `/api/admin/train-envs/${encodeURIComponent(slug)}/packs`,
    { packs },
  );

export const getTrainEnvPassword = () =>
  api<{ password: string; error?: string }>('GET', '/api/me/train-env/password');

// ── Graded assignments (checked on the learner's Mitti) and Khalifa sign-off ──
export interface AssignmentTask {
  n: number;
  title: string;
  hint: string;
}
export interface AssignmentResult {
  ok: boolean;
  /** inconclusive: could not be verified on this Mitti's version; counted in the learner's favour. */
  status?: 'pass' | 'fail' | 'inconclusive';
  notes: string[];
  answer?: string;
  at: string;
}
export interface AssignmentState {
  title: string;
  pass: { min: number; must: number[] };
  tasks: AssignmentTask[];
  results: Record<string, AssignmentResult>;
  error?: string;
}
export const getAssignment = (trackId: string, stepId: string) =>
  api<AssignmentState>('GET', `/api/me/assignments/${encodeURIComponent(trackId)}/${encodeURIComponent(stepId)}`);

export const checkAssignmentTask = (trackId: string, stepId: string, task: number) =>
  api<{
    result: AssignmentResult & { n: number };
    results: Record<string, AssignmentResult>;
    passed: boolean;
    completion: CompleteStepResult | null;
    error?: string;
  }>('POST', '/api/me/assignments/check', { trackId, stepId, task });

export const signoffViva = (trackId: string, stepId: string, email: string, note: string) =>
  api<{ ok: boolean; alreadyDone?: boolean; error?: string }>('POST', '/api/admin/assignments/signoff', {
    trackId,
    stepId,
    email,
    note,
  });
